import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../ui/form';
import { Input } from '../../ui/input';
import AddressInput from '../../Inputs/AddressInput';

interface CreateCarrierHub {
  name: string;
  address: string;
  addressObject: any;
  addressLat: number;
  addressLng: number;
  carrierId: number;
}

const formSchema = z.object({
  name: z.string().min(1, 'Hub name is required'),
  address: z
    .object({
      address: z.string().min(1, 'Address is required'),
      addressObject: z.any().optional(),
      latLng: z.object({
        lat: z.number(),
        lng: z.number(),
      }),
    })
    .refine(
      (data) => {
        // Check if address is selected
        if (!data.address || !data.latLng.lat || !data.latLng.lng) {
          return false;
        }

        // Check if addressObject exists and has required fields
        if (!data.addressObject) {
          return false;
        }

        // Validate that address has house number
        if (!data.addressObject.houseNumber) {
          return false;
        }

        // Validate that address has postal code
        if (!data.addressObject.postalCode) {
          return false;
        }

        return true;
      },
      {
        message: 'Please select a complete address with house number and postal code from the dropdown',
        path: ['address'],
      },
    ),
});

type FormValues = z.infer<typeof formSchema>;

interface CarrierHubDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carrierId: number;
  onSubmit: (data: CreateCarrierHub) => void;
}

export const CarrierHubDialog: React.FC<CarrierHubDialogProps> = ({ open, onOpenChange, carrierId, onSubmit }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      address: {
        address: '',
        addressObject: null,
        latLng: { lat: null, lng: null },
      },
    },
  });

  const handleSubmit = (data: FormValues) => {
    const createInput: CreateCarrierHub = {
      name: data.name,
      address: data.address.address,
      addressObject: data.address.addressObject,
      addressLat: data.address.latLng.lat,
      addressLng: data.address.latLng.lng,
      carrierId,
    };

    onSubmit(createInput);
    form.reset();
    onOpenChange(false);
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Carrier Hub</DialogTitle>
          <DialogDescription>
            Add a new hub location for this carrier. Provide a name and select a complete address with house number and
            postal code.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hub Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter hub name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address (Street, number, Postal code, city, country)</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <AddressInput
                        placeholder="Enter address (select from dropdown)"
                        value={field.value}
                        onChange={field.onChange}
                        onSelect={field.onChange}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={!form.formState.isValid}>
                Create Hub
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
